import { type Club, RecurringSession } from "@mixitone/models";
import { formatTime } from "@mixitone/util";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { type PrintPaletteColors, paletteColors, rgbToHex } from "./clubDirectoryPrintPalettes";

export type ClubDirectoryPdfSession = {
  weekday: number;
  startTime?: string | null;
  endTime?: string | null;
  playerFee?: number | null;
  description?: string | null;
};

export type ClubDirectoryPdfClub = {
  name: string;
  address?: string | null;
  region?: string | null;
  country?: string | null;
  sportType?: string | null;
  telephone?: string | null;
  email?: string | null;
  webpage?: string | null;
  facebookPage?: string | null;
};

const PDF_LOCALE = "en-NZ";

const PAGE = {
  topBand: 6,
  footer: 15,
  gutter: 14,
  sectionGap: 8,
} as const;

const MAST = {
  minHeight: 52,
  padX: 16,
  padTop: 16,
  padBottom: 14,
} as const;

const SESSION = {
  titleBar: 11,
  rowGap: 4,
  rowPadY: 5,
  tileWidth: 26,
  tileMinHeight: 20,
  contentPadX: 7,
} as const;

const FOOT = {
  minHeight: 64,
  columnGap: 8,
  qrColumnWidth: 66,
  padX: 9,
  padY: 9,
  contactHeader: 8,
  contactItemGap: 2,
} as const;

const TYPO = {
  clubName: 34,
  clubNameLine: 13,
  address: 12,
  addressLine: 5.5,
  sectionTitle: 12,
  dayAbbrev: 15,
  sessionTime: 14,
  sessionFee: 10,
  sessionDescription: 10.5,
  sessionDescriptionLine: 4.6,
  contactLabel: 9,
  contactValue: 11,
  contactLine: 5.6,
  qrCta: 13,
  qrUrl: 8.5,
  qrUrlLine: 3.8,
} as const;

export function formatDirectoryAddress(
  address: string | null | undefined,
  region: string | null | undefined,
  country: string | null | undefined,
): string | null {
  if (address) {
    const parts = [address, region, country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  }
  if (region) return region;
  return null;
}

export function formatSportTypeLabel(sportType: string | null | undefined): string | null {
  if (!sportType) return null;
  return sportType.charAt(0).toUpperCase() + sportType.slice(1);
}

export function formatSessionTimeRange(session: ClubDirectoryPdfSession): string {
  const start = session.startTime ? formatTime(session.startTime, PDF_LOCALE) : "";
  const end = session.endTime ? formatTime(session.endTime, PDF_LOCALE) : "";
  if (start && end) return `${start} - ${end}`;
  if (start) return `from ${start}`;
  return "";
}

export function formatSessionFee(playerFee: number | null | undefined): string {
  if (playerFee == null || Number.isNaN(playerFee)) return "";
  return ` ($${playerFee})`;
}

export function weekdayLabel(weekday: number): string {
  return RecurringSession.WEEKDAYS[weekday - 1] ?? `Day ${weekday}`;
}

export function weekdayAbbrev(weekday: number): string {
  return weekdayLabel(weekday).slice(0, 3).toUpperCase();
}

export function formatCardSessionLine(session: ClubDirectoryPdfSession): string {
  const day = weekdayLabel(session.weekday).slice(0, 3);
  const time = formatSessionTimeRange(session);
  const fee = formatSessionFee(session.playerFee);
  return `${day} ${time}${fee}`.replace(/\s+/g, " ").trim();
}

export function groupSessionsByWeekday(
  sessions: ClubDirectoryPdfSession[],
): Map<number, ClubDirectoryPdfSession[]> {
  const grouped = new Map<number, ClubDirectoryPdfSession[]>();
  for (const session of sessions) {
    const existing = grouped.get(session.weekday) ?? [];
    existing.push(session);
    grouped.set(session.weekday, existing);
  }
  return grouped;
}

export function orderedSessions(sessions: ClubDirectoryPdfSession[]): ClubDirectoryPdfSession[] {
  const grouped = groupSessionsByWeekday(sessions);
  const sortedDays = [...grouped.keys()].sort((a, b) => a - b);
  const flat: ClubDirectoryPdfSession[] = [];
  for (const day of sortedDays) flat.push(...(grouped.get(day) ?? []));
  return flat;
}

export function buildClubDirectoryPdfContent(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
): string[] {
  const lines: string[] = [];
  lines.push(club.name);

  const address = formatDirectoryAddress(club.address, club.region, club.country);
  if (address) lines.push(address);

  const sport = formatSportTypeLabel(club.sportType);
  if (sport) lines.push(sport);

  if (sessions.length > 0) {
    lines.push("Sessions");
    for (const session of orderedSessions(sessions)) {
      const timeRange = formatSessionTimeRange(session);
      const fee = formatSessionFee(session.playerFee);
      lines.push(`${weekdayLabel(session.weekday)}: ${timeRange}${fee}`.trim());
      if (session.description?.trim()) {
        for (const paragraph of session.description.split(/\n\s*\n/).filter((part) => part.trim())) {
          lines.push(paragraph.trim());
        }
      }
    }
  }

  const contactLines: string[] = [];
  if (club.telephone?.trim()) contactLines.push(`Phone: ${club.telephone.trim()}`);
  if (club.email?.trim()) contactLines.push(`Email: ${club.email.trim()}`);
  if (club.webpage?.trim()) contactLines.push(`Website: ${club.webpage.trim()}`);
  if (club.facebookPage?.trim()) contactLines.push(`Facebook: ${club.facebookPage.trim()}`);
  if (contactLines.length > 0) {
    lines.push("Contact");
    lines.push(...contactLines);
  }

  return lines;
}

export function clubDirectoryPdfClubFromRecord(club: Club): ClubDirectoryPdfClub {
  return {
    name: club.name ?? "Club",
    address: club.address,
    region: club.region,
    country: club.country,
    sportType: club.sport_type,
    telephone: club.telephone,
    email: club.email,
    webpage: club.webpage,
    facebookPage: club.facebook_page,
  };
}

export function clubDirectoryPdfSessionsFromRecords(sessions: RecurringSession[]): ClubDirectoryPdfSession[] {
  return sessions.map((session) => ({
    weekday: session.weekday!,
    startTime: session.start_time,
    endTime: session.end_time,
    playerFee: session.player_fee,
    description: session.description,
  }));
}

function safeFilename(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "club-directory";
}

const MIXITONE_BRAND = {
  mix: [107, 114, 128] as const,
  it: [30, 41, 59] as const,
  one: [14, 116, 144] as const,
};

function setRgb(doc: jsPDF, color: readonly [number, number, number]) {
  doc.setTextColor(color[0], color[1], color[2]);
}

function setFill(doc: jsPDF, color: readonly [number, number, number]) {
  doc.setFillColor(color[0], color[1], color[2]);
}

function setDraw(doc: jsPDF, color: readonly [number, number, number]) {
  doc.setDrawColor(color[0], color[1], color[2]);
}

function pageWidth(doc: jsPDF) {
  return doc.internal.pageSize.getWidth();
}

function pageHeight(doc: jsPDF) {
  return doc.internal.pageSize.getHeight();
}

function wrappedLineCount(doc: jsPDF, text: string, maxWidth: number): number {
  return Math.max(1, (doc.splitTextToSize(text, maxWidth) as string[]).length);
}

function writeLines(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  color: readonly [number, number, number],
): number {
  setRgb(doc, color);
  for (const line of doc.splitTextToSize(text, maxWidth) as string[]) {
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function writeLimitedLines(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  color: readonly [number, number, number],
  maxLines: number,
): number {
  setRgb(doc, color);
  const lines = (doc.splitTextToSize(text, maxWidth) as string[]).slice(0, maxLines);
  for (const line of lines) {
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function fillPolygon(doc: jsPDF, points: Array<[number, number]>, color: readonly [number, number, number]) {
  if (points.length < 3) return;
  setFill(doc, color);
  const first = points[0]!;
  const deltas = points.slice(1).map((point, index) => {
    const prev = points[index]!;
    return [point[0] - prev[0], point[1] - prev[1]] as [number, number];
  });
  doc.lines(deltas, first[0], first[1], [1, 1], "F", true);
}

function drawTopBand(doc: jsPDF, C: PrintPaletteColors) {
  const width = pageWidth(doc);
  setFill(doc, C.cyan);
  doc.rect(0, 0, width, PAGE.topBand, "F");
  setFill(doc, C.cyanDeep);
  doc.rect(0, 0, width * 0.4, PAGE.topBand, "F");
  setFill(doc, C.cyanMuted);
  doc.rect(width * 0.4, 0, width * 0.14, PAGE.topBand, "F");
}

function drawFooter(doc: jsPDF, C: PrintPaletteColors) {
  const width = pageWidth(doc);
  const height = pageHeight(doc);
  const y = height - PAGE.footer;

  setDraw(doc, C.slate200);
  doc.setLineWidth(0.3);
  doc.line(PAGE.gutter, y + 4, width - PAGE.gutter, y + 4);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const prefix = "Powered by ";
  const mix = "Mix";
  const it = "It";
  const one = "One";
  const total =
    doc.getTextWidth(prefix) + doc.getTextWidth(mix) + doc.getTextWidth(it) + doc.getTextWidth(one);
  let x = width / 2 - total / 2;
  const baseline = y + 10;

  setRgb(doc, C.slate500);
  doc.text(prefix, x, baseline);
  x += doc.getTextWidth(prefix);
  setRgb(doc, MIXITONE_BRAND.mix);
  doc.text(mix, x, baseline);
  x += doc.getTextWidth(mix);
  setRgb(doc, MIXITONE_BRAND.it);
  doc.text(it, x, baseline);
  x += doc.getTextWidth(it);
  setRgb(doc, MIXITONE_BRAND.one);
  doc.text(one, x, baseline);
}

function measureMastheadHeight(doc: jsPDF, club: ClubDirectoryPdfClub, innerWidth: number): number {
  let height = MAST.padTop + MAST.padBottom;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.clubName);
  height += wrappedLineCount(doc, club.name, innerWidth) * TYPO.clubNameLine;

  const address = formatDirectoryAddress(club.address, club.region, club.country);
  if (address) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.address);
    height += 4 + wrappedLineCount(doc, address, innerWidth) * TYPO.addressLine;
  }

  return Math.max(MAST.minHeight, height);
}

function drawMasthead(
  doc: jsPDF,
  club: ClubDirectoryPdfClub,
  y: number,
  height: number,
  C: PrintPaletteColors,
) {
  const width = pageWidth(doc);
  setFill(doc, C.slate900);
  doc.rect(0, y, width, height, "F");

  // Coherent diagonal cyan motif anchored bottom-right.
  fillPolygon(
    doc,
    [
      [width * 0.62, y + height],
      [width, y + height * 0.35],
      [width, y + height],
    ],
    C.cyanDark,
  );
  fillPolygon(
    doc,
    [
      [width * 0.78, y + height],
      [width, y + height * 0.62],
      [width, y + height],
    ],
    C.cyan,
  );

  const textX = PAGE.gutter + 2;
  const innerWidth = width - textX - MAST.padX - width * 0.2;

  let textY = y + MAST.padTop;

  setRgb(doc, C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.clubName);
  textY = writeLines(doc, club.name, textX, textY + 6, innerWidth, TYPO.clubNameLine, C.white);

  const address = formatDirectoryAddress(club.address, club.region, club.country);
  if (address) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.address);
    writeLines(doc, address, textX, textY + 2, innerWidth, TYPO.addressLine, C.cyanMuted);
  }
}

function drawSectionTab(
  doc: jsPDF,
  title: string,
  x: number,
  y: number,
  width: number,
  C: PrintPaletteColors,
): number {
  setFill(doc, C.slate900);
  doc.rect(x, y, width, SESSION.titleBar, "F");
  setFill(doc, C.cyan);
  doc.rect(x, y, 4, SESSION.titleBar, "F");

  setRgb(doc, C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.sectionTitle);
  doc.text(title.toUpperCase(), x + 9, y + SESSION.titleBar / 2 + 2.2);

  return y + SESSION.titleBar;
}

function sessionContentHeight(doc: jsPDF, session: ClubDirectoryPdfSession, contentWidth: number): number {
  let content = TYPO.sessionTime;
  if (session.description?.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.sessionDescription);
    content += 2;
    for (const paragraph of session.description.split(/\n\s*\n/).filter((p) => p.trim())) {
      content += wrappedLineCount(doc, paragraph.trim(), contentWidth) * TYPO.sessionDescriptionLine;
      content += 1.5;
    }
  }
  return content;
}

function measureSessionRowHeight(doc: jsPDF, session: ClubDirectoryPdfSession, contentWidth: number): number {
  const content = SESSION.rowPadY * 2 + sessionContentHeight(doc, session, contentWidth);
  return Math.max(SESSION.tileMinHeight, content);
}

function drawSessionRow(
  doc: jsPDF,
  session: ClubDirectoryPdfSession,
  x: number,
  y: number,
  width: number,
  rowHeight: number,
  C: PrintPaletteColors,
) {
  // White row body with border.
  setFill(doc, C.white);
  setDraw(doc, C.slate200);
  doc.setLineWidth(0.3);
  doc.rect(x, y, width, rowHeight, "FD");

  // Cyan day tile on the left, coherent with masthead accent.
  setFill(doc, C.cyan);
  doc.rect(x, y, SESSION.tileWidth, rowHeight, "F");

  const tileCenterX = x + SESSION.tileWidth / 2;
  setRgb(doc, C.onAccent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.dayAbbrev);
  doc.text(weekdayAbbrev(session.weekday), tileCenterX, y + rowHeight / 2 + 1.5, { align: "center" });

  const contentX = x + SESSION.tileWidth + SESSION.contentPadX;
  const contentWidth = width - SESSION.tileWidth - SESSION.contentPadX * 2;
  const contentH = sessionContentHeight(doc, session, contentWidth);
  let contentY = y + Math.max(SESSION.rowPadY, (rowHeight - contentH) / 2) + TYPO.sessionTime - 3.5;

  const timeRange = formatSessionTimeRange(session);
  setRgb(doc, C.slate900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.sessionTime);
  const timeWidth = timeRange ? doc.getTextWidth(timeRange) : 0;
  if (timeRange) doc.text(timeRange, contentX, contentY);

  if (session.playerFee != null && !Number.isNaN(session.playerFee)) {
    const feeLabel = `$${session.playerFee}`;
    doc.setFontSize(TYPO.sessionFee);
    const padPill = 2.4;
    const pillWidth = doc.getTextWidth(feeLabel) + padPill * 2;
    const pillX = contentX + (timeRange ? timeWidth + 4 : 0);
    const pillY = contentY - TYPO.sessionFee + 1.5;
    setFill(doc, C.cyanWash);
    setDraw(doc, C.cyanMuted);
    doc.setLineWidth(0.3);
    doc.roundedRect(pillX, pillY, pillWidth, TYPO.sessionFee - 0.5, 1.2, 1.2, "FD");
    setRgb(doc, C.cyanDark);
    doc.setFont("helvetica", "bold");
    doc.text(feeLabel, pillX + padPill, contentY - 1.4);
  }

  contentY += 2;
  if (session.description?.trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.sessionDescription);
    for (const paragraph of session.description.split(/\n\s*\n/).filter((p) => p.trim())) {
      contentY = writeLines(
        doc,
        paragraph.trim(),
        contentX,
        contentY + TYPO.sessionDescriptionLine,
        contentWidth,
        TYPO.sessionDescriptionLine,
        C.slate700,
      );
      contentY += 1.5;
    }
  }
}

const CONTACT_LABEL_COL = 22;

function measureContactHeight(
  doc: jsPDF,
  items: Array<{ label: string; value: string }>,
  innerWidth: number,
): number {
  let height = FOOT.padY + 4 + FOOT.contactHeader;
  const valueWidth = innerWidth - CONTACT_LABEL_COL;
  for (const item of items) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.contactValue);
    const lines = Math.max(1, wrappedLineCount(doc, item.value, valueWidth));
    height += lines * TYPO.contactLine + FOOT.contactItemGap;
  }
  return height + FOOT.padY;
}

function drawSessionsSection(
  doc: jsPDF,
  sessions: ClubDirectoryPdfSession[],
  x: number,
  top: number,
  width: number,
  regionBottom: number,
  C: PrintPaletteColors,
) {
  const tabBottom = top + SESSION.titleBar + 5;
  const available = regionBottom - tabBottom;

  // Use two columns for busier schedules so everything fits one page and reads
  // like a grid; single column keeps roomy rows for a handful of sessions.
  const columns = sessions.length >= 4 ? 2 : 1;
  const colGap = columns === 2 ? SESSION.rowGap : 0;
  const colWidth = (width - colGap * (columns - 1)) / columns;
  const cellContentWidth = colWidth - SESSION.tileWidth - SESSION.contentPadX * 2;

  const cellHeights = sessions.map((session) => measureSessionRowHeight(doc, session, cellContentWidth));

  const gridRows = Math.ceil(sessions.length / columns);
  const rowHeights: number[] = [];
  for (let r = 0; r < gridRows; r++) {
    let h = 0;
    for (let c = 0; c < columns; c++) {
      const idx = r * columns + c;
      if (idx < sessions.length) h = Math.max(h, cellHeights[idx]!);
    }
    rowHeights.push(h);
  }

  const gapsTotal = SESSION.rowGap * (gridRows - 1);
  const rowsNatural = rowHeights.reduce((sum, h) => sum + h, 0) + gapsTotal;

  // Distribute spare space (grow) or gently compress to keep a single page.
  let extraPerRow = 0;
  if (available > rowsNatural) {
    const cap = 14 * gridRows;
    extraPerRow = Math.min(available - rowsNatural, cap) / gridRows;
  } else if (rowsNatural > available && gridRows > 0) {
    extraPerRow = (available - rowsNatural) / gridRows;
  }

  const adjustedRowHeights = rowHeights.map((h) => Math.max(SESSION.tileMinHeight * 0.7, h + extraPerRow));
  const rowsTotal = adjustedRowHeights.reduce((sum, h) => sum + h, 0) + gapsTotal;
  const blockHeight = SESSION.titleBar + 5 + rowsTotal;
  const blockTop = top + Math.max(0, (available + SESSION.titleBar + 5 - blockHeight) / 2);

  let cursorY = drawSectionTab(doc, "Weekly Sessions", x, blockTop, width, C) + 5;
  for (let r = 0; r < gridRows; r++) {
    if (r > 0) cursorY += SESSION.rowGap;
    const rowHeight = adjustedRowHeights[r]!;
    for (let c = 0; c < columns; c++) {
      const idx = r * columns + c;
      if (idx >= sessions.length) continue;
      const cellX = x + c * (colWidth + colGap);
      drawSessionRow(doc, sessions[idx]!, cellX, cursorY, colWidth, rowHeight, C);
    }
    cursorY += rowHeight;
  }
}

function drawContactPanel(
  doc: jsPDF,
  items: Array<{ label: string; value: string }>,
  x: number,
  y: number,
  width: number,
  height: number,
  C: PrintPaletteColors,
) {
  setFill(doc, C.slate900);
  doc.rect(x, y, width, height, "F");
  setFill(doc, C.cyan);
  doc.rect(x, y, width, 4, "F");

  const textX = x + FOOT.padX;
  const innerWidth = width - FOOT.padX * 2;
  const valueX = textX + CONTACT_LABEL_COL;
  const valueWidth = innerWidth - CONTACT_LABEL_COL;
  let contactY = y + FOOT.padY + 4;

  setRgb(doc, C.cyanMuted);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.sectionTitle);
  doc.text("GET IN TOUCH", textX, contactY);
  contactY += FOOT.contactHeader;

  for (const item of items) {
    setRgb(doc, C.cyanMuted);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(TYPO.contactLabel);
    doc.text(item.label.toUpperCase(), textX, contactY);

    setRgb(doc, C.white);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TYPO.contactValue);
    const lines = doc.splitTextToSize(item.value, valueWidth) as string[];
    doc.text(lines[0] ?? "", valueX, contactY);
    contactY += TYPO.contactLine;
    for (let i = 1; i < lines.length; i++) {
      doc.text(lines[i]!, valueX, contactY);
      contactY += TYPO.contactLine;
    }
    contactY += FOOT.contactItemGap;
  }
}

function drawQrPanel(
  doc: jsPDF,
  qrDataUrl: string,
  listingUrl: string,
  x: number,
  y: number,
  width: number,
  height: number,
  C: PrintPaletteColors,
) {
  setFill(doc, C.cyan);
  doc.rect(x, y, width, height, "F");
  fillPolygon(
    doc,
    [
      [x, y],
      [x + width, y],
      [x + width, y + 12],
      [x, y + 22],
    ],
    C.cyanDark,
  );

  const centerX = x + width / 2;
  const innerWidth = width - FOOT.padX * 2;

  setRgb(doc, C.onAccent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TYPO.qrCta);
  doc.text("SCAN TO VIEW", centerX, y + 11, { align: "center" });
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "normal");
  setRgb(doc, C.onAccent);
  doc.text("our full club listing", centerX, y + 16.5, { align: "center" });

  const urlLineCount = (doc.splitTextToSize(listingUrl, innerWidth) as string[]).length;
  const urlBlock = urlLineCount * TYPO.qrUrlLine + 6;
  const qrTop = y + 21;
  const qrSize = Math.min(width - 18, height - 21 - urlBlock - 4);
  const qrX = centerX - qrSize / 2;
  const qrY = qrTop;

  setFill(doc, C.white);
  doc.roundedRect(qrX - 3, qrY - 3, qrSize + 6, qrSize + 6, 2, 2, "F");
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

  setRgb(doc, C.onAccent);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(TYPO.qrUrl);
  let urlY = qrY + qrSize + 7;
  for (const line of doc.splitTextToSize(listingUrl, innerWidth) as string[]) {
    doc.text(line, centerX, urlY, { align: "center" });
    urlY += TYPO.qrUrlLine;
  }
}

export async function buildClubDirectoryPdfDoc(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
  listingUrl: string,
  paletteId?: string | null,
): Promise<jsPDF> {
  const C = paletteColors(paletteId);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const width = pageWidth(doc);
  const height = pageHeight(doc);

  // Page background wash for cohesion.
  setFill(doc, C.cyanWash);
  doc.rect(0, 0, width, height, "F");
  setFill(doc, C.white);
  doc.rect(0, 0, width, height, "F");

  drawTopBand(doc, C);

  const contactItems: Array<{ label: string; value: string }> = [];
  if (club.telephone?.trim()) contactItems.push({ label: "Phone", value: club.telephone.trim() });
  if (club.email?.trim()) contactItems.push({ label: "Email", value: club.email.trim() });
  if (club.webpage?.trim()) contactItems.push({ label: "Website", value: club.webpage.trim() });
  if (club.facebookPage?.trim()) contactItems.push({ label: "Facebook", value: club.facebookPage.trim() });

  const orderedList = orderedSessions(sessions);

  // Masthead.
  const mastInnerWidth = width - (PAGE.gutter + 2) - MAST.padX - width * 0.2;
  const mastTop = PAGE.topBand;
  const mastHeight = measureMastheadHeight(doc, club, mastInnerWidth);
  drawMasthead(doc, club, mastTop, mastHeight, C);

  // Footer band (contact + QR) pinned to the bottom, above the powered-by line.
  const footLeftX = PAGE.gutter;
  const footWidth = width - PAGE.gutter * 2;
  const qrWidth = FOOT.qrColumnWidth;
  const contactWidth = footWidth - qrWidth - FOOT.columnGap;
  const qrX = footLeftX + contactWidth + FOOT.columnGap;
  const contactInnerWidth = contactWidth - FOOT.padX * 2;
  const contactNeeded =
    contactItems.length > 0 ? measureContactHeight(doc, contactItems, contactInnerWidth) : 0;
  const footHeight = Math.max(FOOT.minHeight, contactNeeded);
  const footTop = height - PAGE.footer - footHeight;

  const qrDataUrl = await QRCode.toDataURL(listingUrl, {
    margin: 1,
    width: 640,
    color: { dark: rgbToHex(C.slate900), light: "#ffffff" },
  });

  // Sessions occupy the space between the masthead and the footer band.
  const sessionsTop = mastTop + mastHeight + PAGE.sectionGap;

  if (orderedList.length > 0) {
    const regionBottom = footTop - PAGE.sectionGap;
    drawSessionsSection(doc, orderedList, footLeftX, sessionsTop, footWidth, regionBottom, C);
  }

  if (contactItems.length > 0) {
    drawContactPanel(doc, contactItems, footLeftX, footTop, contactWidth, footHeight, C);
  }
  drawQrPanel(doc, qrDataUrl, listingUrl, qrX, footTop, qrWidth, footHeight, C);

  drawFooter(doc, C);

  return doc;
}

export async function createClubDirectoryPdfBlob(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
  listingUrl: string,
  paletteId?: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const doc = await buildClubDirectoryPdfDoc(club, sessions, listingUrl, paletteId);
  return { blob: doc.output("blob"), filename: `${safeFilename(club.name)}-directory.pdf` };
}

export function savePdfBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadClubDirectoryPdf(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
  listingUrl: string,
  paletteId?: string | null,
): Promise<void> {
  const { blob, filename } = await createClubDirectoryPdfBlob(club, sessions, listingUrl, paletteId);
  savePdfBlob(blob, filename);
}

const CARD = {
  width: 85,
  height: 55,
  topBand: 3,
  padX: 4.5,
  padY: 5.5,
  qrColumnWidth: 34,
  qrInset: 2.8,
  textGap: 3,
} as const;

const CARD_TYPO = {
  clubName: 12,
  clubNameLine: 5.2,
  address: 7.5,
  addressLine: 3.4,
  contact: 7,
  contactLine: 3.3,
  sessionHeader: 6.5,
  session: 6.5,
  sessionLine: 3.1,
  qrCta: 6.5,
} as const;

function cardContactLines(club: ClubDirectoryPdfClub): string[] {
  const lines: string[] = [];
  if (club.telephone?.trim()) lines.push(club.telephone.trim());
  const webpage = club.webpage?.trim();
  if (webpage) {
    lines.push(webpage.replace(/^https?:\/\//i, "").replace(/\/$/, ""));
  } else if (club.email?.trim()) {
    lines.push(club.email.trim());
  }
  return lines.slice(0, 2);
}

export async function buildClubDirectoryCardPdfDoc(
  club: ClubDirectoryPdfClub,
  listingUrl: string,
  sessions: ClubDirectoryPdfSession[] = [],
  paletteId?: string | null,
): Promise<jsPDF> {
  const C = paletteColors(paletteId);
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [CARD.width, CARD.height] });
  const width = pageWidth(doc);
  const height = pageHeight(doc);

  setFill(doc, C.white);
  doc.rect(0, 0, width, height, "F");

  setFill(doc, C.cyan);
  doc.rect(0, 0, width, CARD.topBand, "F");
  setFill(doc, C.cyanDeep);
  doc.rect(0, 0, width * 0.4, CARD.topBand, "F");
  setFill(doc, C.cyanMuted);
  doc.rect(width * 0.4, 0, width * 0.14, CARD.topBand, "F");

  const qrColX = width - CARD.qrColumnWidth;
  const qrColTop = CARD.topBand;
  const qrColHeight = height - CARD.topBand;

  setFill(doc, C.cyan);
  doc.rect(qrColX, qrColTop, CARD.qrColumnWidth, qrColHeight, "F");
  fillPolygon(
    doc,
    [
      [qrColX, qrColTop],
      [width, qrColTop],
      [width, qrColTop + 10],
      [qrColX, qrColTop + 16],
    ],
    C.cyanDark,
  );

  const qrDataUrl = await QRCode.toDataURL(listingUrl, {
    margin: 1,
    width: 512,
    color: { dark: rgbToHex(C.slate900), light: "#ffffff" },
  });

  const qrSize = CARD.qrColumnWidth - CARD.qrInset * 2;
  const ctaSpace = 6;
  const qrBlockHeight = qrSize + 1.5 + ctaSpace;
  const qrY = qrColTop + (qrColHeight - qrBlockHeight) / 2;
  const qrX = qrColX + CARD.qrInset;

  setFill(doc, C.white);
  doc.roundedRect(qrX - 1.2, qrY - 1.2, qrSize + 2.4, qrSize + 2.4, 1.2, 1.2, "F");
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

  const qrCenterX = qrColX + CARD.qrColumnWidth / 2;
  setRgb(doc, C.onAccent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(CARD_TYPO.qrCta);
  doc.text("SCAN TO VIEW", qrCenterX, qrY + qrSize + 5, { align: "center" });

  const textX = CARD.padX;
  const textWidth = qrColX - CARD.padX - CARD.textGap;
  const address = formatDirectoryAddress(club.address, club.region, club.country);
  const contacts = cardContactLines(club);

  let textY = CARD.topBand + CARD.padY + CARD_TYPO.clubNameLine - 1.5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(CARD_TYPO.clubName);
  textY = writeLimitedLines(doc, club.name, textX, textY, textWidth, CARD_TYPO.clubNameLine, C.slate900, 2);

  if (address) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(CARD_TYPO.address);
    textY = writeLimitedLines(
      doc,
      address,
      textX,
      textY + 0.6,
      textWidth,
      CARD_TYPO.addressLine,
      C.slate700,
      2,
    );
  }

  if (contacts.length > 0) {
    textY += 1.4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(CARD_TYPO.contact);
    for (const line of contacts) {
      textY = writeLimitedLines(doc, line, textX, textY, textWidth, CARD_TYPO.contactLine, C.cyanDeep, 1);
    }
  }

  const orderedList = orderedSessions(sessions);
  const bottomLimit = height - 3.2;
  const sessionGap = 2.2;
  const headerHeight = 3.4;
  const available = bottomLimit - textY - sessionGap - headerHeight;

  if (orderedList.length > 0 && available >= CARD_TYPO.sessionLine) {
    textY += sessionGap;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(CARD_TYPO.sessionHeader);
    setRgb(doc, C.slate500);
    doc.text("SESSIONS", textX, textY);
    textY += headerHeight;

    const columns = orderedList.length >= 4 ? 2 : 1;
    const colGap = columns === 2 ? 2 : 0;
    const colWidth = (textWidth - colGap * (columns - 1)) / columns;
    const maxRows = Math.max(1, Math.floor((bottomLimit - textY) / CARD_TYPO.sessionLine));
    const maxCount = Math.min(orderedList.length, maxRows * columns);
    const visible = orderedList.slice(0, maxCount);
    const gridRows = Math.ceil(visible.length / columns);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(CARD_TYPO.session);
    for (let r = 0; r < gridRows; r++) {
      const rowY = textY + r * CARD_TYPO.sessionLine;
      for (let c = 0; c < columns; c++) {
        const idx = r * columns + c;
        if (idx >= visible.length) continue;
        const cellX = textX + c * (colWidth + colGap);
        writeLimitedLines(
          doc,
          formatCardSessionLine(visible[idx]!),
          cellX,
          rowY,
          colWidth,
          CARD_TYPO.sessionLine,
          C.slate700,
          1,
        );
      }
    }
  }

  return doc;
}

export async function createClubDirectoryCardPdfBlob(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
  listingUrl: string,
  paletteId?: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const doc = await buildClubDirectoryCardPdfDoc(club, listingUrl, sessions, paletteId);
  return { blob: doc.output("blob"), filename: `${safeFilename(club.name)}-card.pdf` };
}

export async function downloadClubDirectoryCardPdf(
  club: ClubDirectoryPdfClub,
  sessions: ClubDirectoryPdfSession[],
  listingUrl: string,
  paletteId?: string | null,
): Promise<void> {
  const { blob, filename } = await createClubDirectoryCardPdfBlob(club, sessions, listingUrl, paletteId);
  savePdfBlob(blob, filename);
}
